"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery } from "@tanstack/react-query";
import type { KeyboardEvent } from "react";
import { useEffect, useMemo, useRef, useState, useId } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faXmark } from "@fortawesome/free-solid-svg-icons";
import { useClickAway, useDebounce, useKeyPressEvent } from "react-use";
import type { CommunityMemberMinimal } from "@/entities/IProfile";
import { commonApiFetch } from "@/services/api/common-api";
import CommonProfileSearchItems from "../profile-search/CommonProfileSearchItems";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getSelectableIdentity, getSelectableIdentityOption, type SelectableIdentityOption } from "@/components/utils/input/profile-search/getSelectableIdentity";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
export enum IdentitySearchSize {
  SM = "SM",
  MD = "MD",
}
const MIN_SEARCH_LENGTH = 3;
type IdentitySearchDraft = {
  readonly value: string;
  readonly baseResolvedDisplayValue: string | null;
  readonly preservedResolvedValues: readonly (string | null)[];
};
export default function IdentitySearch(t0) {
  const $ = _c(87);
  const {
    identity,
    size: t1,
    label: t2,
    error: t3,
    errorMessage,
    autoFocus: t4,
    selectedDisplayValue,
    clearable: t5,
    dropdownListClassName,
    onSelectionChange,
    setIdentity
  } = t0;
  const size = t1 === undefined ? IdentitySearchSize.MD : t1;
  const label = t2 === undefined ? "Identity" : t2;
  const error = t3 === undefined ? false : t3;
  const autoFocus = t4 === undefined ? false : t4;
  const clearable = t5 === undefined ? true : t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      [IdentitySearchSize.SM]: "tw-py-3 tw-text-sm",
      [IdentitySearchSize.MD]: "tw-pb-3 tw-pt-3 tw-text-md"
    };
    $[0] = t6;
  } else {
    t6 = $[0];
  }
  const INPUT_CLASSES = t6;
  let t7;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      [IdentitySearchSize.SM]: "tw-text-sm",
      [IdentitySearchSize.MD]: "tw-text-md"
    };
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const LABEL_CLASSES = t7;
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      [IdentitySearchSize.SM]: "tw-h-4 tw-w-4",
      [IdentitySearchSize.MD]: "tw-h-5 tw-w-5"
    };
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const SEARCH_ICON_SIZE_CLASSES = t8;
  const inputId = useId();
  const listboxId = `${inputId}-listbox`;
  const resolvedDisplayValue = selectedDisplayValue ?? identity ?? null;
  const [searchCriteriaDraft, setSearchCriteriaDraft] = useState(null);
  const shouldUseDraft = searchCriteriaDraft !== null && (searchCriteriaDraft.baseResolvedDisplayValue === resolvedDisplayValue || searchCriteriaDraft.preservedResolvedValues.some(value => value === resolvedDisplayValue));
  const searchCriteria = shouldUseDraft ? searchCriteriaDraft.value : resolvedDisplayValue;
  const [debouncedValue, setDebouncedValue] = useState(searchCriteria);
  let t9;
  if ($[3] !== searchCriteria) {
    t9 = () => setDebouncedValue(searchCriteria);
    $[3] = searchCriteria;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  let t10;
  if ($[5] !== searchCriteria) {
    t10 = [searchCriteria];
    $[5] = searchCriteria;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  useDebounce(t9, 200, t10);
  let t11;
  let t12;
  if ($[7] !== debouncedValue) {
    t11 = [QueryKey.PROFILE_SEARCH, {
      param: debouncedValue,
      only_profile_owners: "true"
    }];
    t12 = async () => await commonApiFetch({
      endpoint: "community-members",
      params: {
        param: debouncedValue ?? "",
        only_profile_owners: "true"
      }
    });
    $[7] = debouncedValue;
    $[8] = t11;
    $[9] = t12;
  } else {
    t11 = $[8];
    t12 = $[9];
  }
  const t13 = !!debouncedValue && debouncedValue.length >= MIN_SEARCH_LENGTH;
  let t14;
  if ($[10] !== t11 || $[11] !== t12 || $[12] !== t13) {
    t14 = {
      queryKey: t11,
      queryFn: t12,
      enabled: t13
    };
    $[10] = t11;
    $[11] = t12;
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const {
    data
  } = useQuery(t14);
  let t15;
  if ($[14] !== data) {
    t15 = data ?? [];
    $[14] = data;
    $[15] = t15;
  } else {
    t15 = $[15];
  }
  const searchResults = t15;
  const [isOpen, setIsOpen] = useState(false);
  const [manualHighlightedIndex, setManualHighlightedIndex] = useState(null);
  const [highlightedOptionId, setHighlightedOptionId] = useState(undefined);
  let t16;
  bb0: {
    if (searchResults.length === 0 || !identity) {
      t16 = null;
      break bb0;
    }
    let t17;
    if ($[16] !== identity) {
      t17 = profile => {
        const value_0 = getSelectableIdentity(profile);
        return value_0?.toLowerCase() === identity.toLowerCase();
      };
      $[16] = identity;
      $[17] = t17;
    } else {
      t17 = $[17];
    }
    const matchingIndex = searchResults.findIndex(t17);
    t16 = matchingIndex >= 0 ? matchingIndex : null;
  }
  const selectedResultIndex = t16;
  let t17;
  bb1: {
    if (!isOpen || searchResults.length === 0) {
      t17 = null;
      break bb1;
    }
    if (manualHighlightedIndex !== null) {
      t17 = Math.min(manualHighlightedIndex, searchResults.length - 1);
      break bb1;
    }
    t17 = selectedResultIndex;
  }
  const effectiveHighlightedIndex = t17;
  let t18;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = () => {
      setIsOpen(false);
      setManualHighlightedIndex(null);
    };
    $[18] = t18;
  } else {
    t18 = $[18];
  }
  const closeDropdown = t18;
  let t19;
  if ($[19] !== onSelectionChange || $[20] !== resolvedDisplayValue || $[21] !== setIdentity) {
    t19 = (newValue, options) => {
      const draftValue = options?.displayValue ?? newValue ?? "";
      setIdentity(newValue);
      onSelectionChange?.(options?.selection ?? null);
      setSearchCriteriaDraft({
        value: draftValue,
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: [newValue, options?.displayValue ?? null]
      });
      closeDropdown();
    };
    $[19] = onSelectionChange;
    $[20] = resolvedDisplayValue;
    $[21] = setIdentity;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  const onValueChange = t19;
  let t20;
  if ($[23] !== searchCriteria?.length) {
    t20 = newV => {
      if (newV) {
        const len = searchCriteria?.length ?? 0;
        setIsOpen(len >= MIN_SEARCH_LENGTH);
        return;
      }
      closeDropdown();
    };
    $[23] = searchCriteria?.length;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  const onFocusChange = t20;
  let t21;
  if ($[25] !== clearable || $[26] !== onSelectionChange || $[27] !== resolvedDisplayValue || $[28] !== setIdentity) {
    t21 = newV_0 => {
      setSearchCriteriaDraft({
        value: newV_0 ?? "",
        baseResolvedDisplayValue: resolvedDisplayValue,
        preservedResolvedValues: []
      });
      const len_0 = newV_0?.length ?? 0;
      setIsOpen(len_0 >= MIN_SEARCH_LENGTH);
      if (!newV_0 && clearable) {
        setIdentity(null);
        onSelectionChange?.(null);
      }
      setManualHighlightedIndex(null);
    };
    $[25] = clearable;
    $[26] = onSelectionChange;
    $[27] = resolvedDisplayValue;
    $[28] = setIdentity;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  const onSearchCriteriaChange = t21;
  let t22;
  if ($[30] !== onValueChange) {
    t22 = profile_0 => {
      const nextSelection = getSelectableIdentityOption(profile_0);
      if (!nextSelection) {
        return false;
      }
      onValueChange(nextSelection.value, {
        displayValue: nextSelection.label,
        selection: nextSelection
      });
      return true;
    };
    $[30] = onValueChange;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  const selectProfile = t22;
  const wrapperRef = useRef(null);
  useClickAway(wrapperRef, closeDropdown);
  useKeyPressEvent("Escape", closeDropdown);
  const inputRef = useRef(null);
  const shouldAutoFocus = useRef(autoFocus);
  let t23;
  let t24;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => {
      if (shouldAutoFocus.current) {
        inputRef.current?.focus();
      }
    };
    t24 = [];
    $[32] = t23;
    $[33] = t24;
  } else {
    t23 = $[32];
    t24 = $[33];
  }
  useEffect(t23, t24);
  let t25;
  if ($[34] !== effectiveHighlightedIndex || $[35] !== searchResults || $[36] !== selectProfile) {
    t25 = event => {
      const resultCount = searchResults.length;
      if (resultCount === 0) {
        return;
      }
      const maxIndex = resultCount - 1;
      const currentHighlightIndex = effectiveHighlightedIndex;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setIsOpen(true);
        setManualHighlightedIndex(currentHighlightIndex === null || currentHighlightIndex >= maxIndex ? 0 : currentHighlightIndex + 1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setIsOpen(true);
        setManualHighlightedIndex(currentHighlightIndex === null || currentHighlightIndex <= 0 ? maxIndex : currentHighlightIndex - 1);
        return;
      }
      if (event.key === "Enter" && effectiveHighlightedIndex !== null) {
        event.preventDefault();
        const profile_1 = searchResults[effectiveHighlightedIndex];
        if (profile_1 !== undefined && selectProfile(profile_1)) {
          inputRef.current?.form?.requestSubmit();
        }
      }
    };
    $[34] = effectiveHighlightedIndex;
    $[35] = searchResults;
    $[36] = selectProfile;
    $[37] = t25;
  } else {
    t25 = $[37];
  }
  const handleArrowNavigation = t25;
  const hasIdentity = identity !== null && identity.length > 0;
  const t26 = searchCriteria ?? "";
  let t27;
  if ($[38] !== onSearchCriteriaChange) {
    t27 = e => onSearchCriteriaChange(e.target.value);
    $[38] = onSearchCriteriaChange;
    $[39] = t27;
  } else {
    t27 = $[39];
  }
  let t28;
  let t29;
  if ($[40] !== onFocusChange) {
    t28 = () => onFocusChange(true);
    t29 = e_0 => {
      const next = e_0.relatedTarget as Node | null;
      if (!next || !wrapperRef.current?.contains(next)) {
        onFocusChange(false);
      }
    };
    $[40] = onFocusChange;
    $[41] = t28;
    $[42] = t29;
  } else {
    t28 = $[41];
    t29 = $[42];
  }
  let t30;
  if ($[43] !== handleArrowNavigation) {
    t30 = event_0 => handleArrowNavigation(event_0);
    $[43] = handleArrowNavigation;
    $[44] = t30;
  } else {
    t30 = $[44];
  }
  const t31 = isOpen && highlightedOptionId ? highlightedOptionId : undefined;
  const t32 = `${INPUT_CLASSES[size]} ${error ? "tw-caret-error tw-ring-error focus:tw-border-error focus:tw-ring-error" : "tw-caret-primary-400 tw-ring-iron-700 hover:tw-ring-iron-650 focus:tw-border-blue-500 focus:tw-ring-primary-400"} tw-peer tw-form-input tw-block tw-w-full tw-appearance-none tw-rounded-lg tw-border-0 tw-border-iron-700 tw-bg-iron-900 tw-pl-9 tw-pr-4 tw-text-base tw-font-medium tw-shadow-sm tw-ring-1 tw-ring-inset tw-transition tw-duration-300 tw-ease-out placeholder:tw-text-iron-500 focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-inset ${searchCriteria ? "tw-text-primary-400 focus:tw-text-white" : "tw-text-white"}`;
  let t33;
  if ($[45] !== inputId || $[46] !== isOpen || $[47] !== listboxId || $[48] !== t26 || $[49] !== t27 || $[50] !== t28 || $[51] !== t29 || $[52] !== t30 || $[53] !== t31 || $[54] !== t32) {
    t33 = <input ref={inputRef} type="text" value={t26} onChange={t27} onFocus={t28} onBlur={t29} onKeyDown={t30} id={inputId} autoComplete="off" role="combobox" aria-autocomplete="list" aria-expanded={isOpen} aria-controls={listboxId} aria-activedescendant={t31} className={t32} placeholder=" " />;
    $[45] = inputId;
    $[46] = isOpen;
    $[47] = listboxId;
    $[48] = t26;
    $[49] = t27;
    $[50] = t28;
    $[51] = t29;
    $[52] = t30;
    $[53] = t31;
    $[54] = t32;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  const t34 = `${"tw-top-3.5"} ${SEARCH_ICON_SIZE_CLASSES[size]} tw-pointer-events-none tw-absolute tw-left-3 tw-text-iron-300`;
  let t35;
  if ($[56] !== t34) {
    t35 = <MagnifyingGlassIcon className={t34} aria-hidden="true" />;
    $[56] = t34;
    $[57] = t35;
  } else {
    t35 = $[57];
  }
  let t36;
  if ($[58] !== clearable || $[59] !== hasIdentity || $[60] !== onValueChange) {
    t36 = clearable && hasIdentity && <button type="button" aria-label="Clear identity" onClick={() => onValueChange(null)} className="tw-top-3.5 tw-absolute tw-right-3 tw-flex tw-h-5 tw-w-5 tw-cursor-pointer tw-items-center tw-justify-center tw-border-0 tw-bg-transparent tw-p-0 tw-text-iron-400 tw-transition tw-duration-300 tw-ease-out hover:tw-text-error focus:tw-outline-none focus:tw-ring-0"><FontAwesomeIcon icon={faXmark} className="tw-h-4 tw-w-4 tw-flex-shrink-0" /></button>;
    $[58] = clearable;
    $[59] = hasIdentity;
    $[60] = onValueChange;
    $[61] = t36;
  } else {
    t36 = $[61];
  }
  const t37 = `${LABEL_CLASSES[size]} ${error ? "peer-placeholder-shown:tw-top-1/4 peer-placeholder-shown:-tw-translate-y-1/4 peer-focus:tw-text-error" : "peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-focus:tw-text-primary-400"} tw-absolute tw-start-1 tw-top-2 tw-z-10 tw-ml-6 tw-origin-[0] -tw-translate-y-4 tw-scale-75 tw-transform tw-cursor-text tw-rounded-lg tw-bg-iron-900 tw-px-2 tw-font-medium tw-text-iron-500 tw-duration-300 peer-placeholder-shown:tw-top-1/2 peer-placeholder-shown:-tw-translate-y-1/2 peer-placeholder-shown:tw-scale-100 peer-focus:tw-top-2 peer-focus:-tw-translate-y-4 peer-focus:tw-scale-75 peer-focus:tw-bg-iron-900 peer-focus:tw-px-2 peer-focus:tw-text-primary-400 rtl:peer-focus:tw-left-auto rtl:peer-focus:tw-translate-x-1/4`;
  let t38;
  if ($[62] !== inputId || $[63] !== label || $[64] !== t37) {
    t38 = <label htmlFor={inputId} className={t37}>{label}</label>;
    $[62] = inputId;
    $[63] = label;
    $[64] = t37;
    $[65] = t38;
  } else {
    t38 = $[65];
  }
  let t39;
  if ($[66] !== selectProfile) {
    t39 = profile_2 => {
      if (!profile_2) {
        return;
      }
      selectProfile(profile_2);
    };
    $[66] = selectProfile;
    $[67] = t39;
  } else {
    t39 = $[67];
  }
  let t40;
  if ($[68] !== dropdownListClassName || $[69] !== effectiveHighlightedIndex || $[70] !== identity || $[71] !== isOpen || $[72] !== listboxId || $[73] !== searchCriteria || $[74] !== searchResults || $[75] !== t39) {
    t40 = <CommonProfileSearchItems open={isOpen} selected={identity} searchCriteria={searchCriteria} profiles={searchResults} highlightedIndex={effectiveHighlightedIndex} listboxId={listboxId} listClassName={dropdownListClassName} onHighlightedOptionIdChange={setHighlightedOptionId} onProfileSelect={t39} />;
    $[68] = dropdownListClassName;
    $[69] = effectiveHighlightedIndex;
    $[70] = identity;
    $[71] = isOpen;
    $[72] = listboxId;
    $[73] = searchCriteria;
    $[74] = searchResults;
    $[75] = t39;
    $[76] = t40;
  } else {
    t40 = $[76];
  }
  let t41;
  if ($[77] !== error || $[78] !== errorMessage) {
    t41 = error && <div className="tw-relative tw-flex tw-items-center tw-gap-x-2 tw-pt-1.5"><FontAwesomeIcon icon={faCircleExclamation} className="tw-h-5 tw-w-5 tw-flex-shrink-0 tw-text-error" aria-hidden="true" /><div className="tw-text-xs tw-font-medium tw-text-error">{errorMessage ?? "Please enter identity"}</div></div>;
    $[77] = error;
    $[78] = errorMessage;
    $[79] = t41;
  } else {
    t41 = $[79];
  }
  let t42;
  if ($[80] !== t33 || $[81] !== t35 || $[82] !== t36 || $[83] !== t38 || $[84] !== t40 || $[85] !== t41) {
    t42 = <div className="tw-group tw-relative tw-w-full" ref={wrapperRef}>{t33}{t35}{t36}{t38}{t40}{t41}</div>;
    $[80] = t33;
    $[81] = t35;
    $[82] = t36;
    $[83] = t38;
    $[84] = t40;
    $[85] = t41;
    $[86] = t42;
  } else {
    t42 = $[86];
  }
  return t42;
}